import { isUserActionableErrorCode } from '@pcp/shared';
import { ApiError } from './api';
import { reportClientError } from './error-reporter';

// Native fetch network-failure messages across browsers. A TypeError with one of
// these IS a real outage; an arbitrary TypeError (a genuine client-side JS bug) is
// NOT — it must surface its real message and be recorded, not hidden as an "outage".
const NETWORK_FAILURE_RE = /failed to fetch|networkerror|network request failed|load failed/i;

/** A 5xx whose body carried no real server message — a blank gateway/proxy
 *  failure (Caddy 502/503/504 with HTML/empty body while the API container rolls
 *  during a deploy), not a real application error. */
function isBlankGatewayError(e: ApiError): boolean {
  if (e.noBody) return true; // request() tagged it: the response body had no JSON
  // Fallback for ApiErrors built elsewhere: the synthetic message request() uses
  // for a bodyless failure, or an empty server-side error.
  return /^Request failed with status \d+$/.test(e.message) || e.message === 'Unknown error';
}

/**
 * True when a failed write was caused by the SERVER being genuinely unreachable
 * or briefly absent (mid-deploy / offline) — NOT a real backend or client error.
 * Used to pick the calm "try again" copy and to decide a failure isn't worth
 * recording. Deliberately narrow:
 *  - statusCode 0 → the request never completed.
 *  - a 5xx ONLY when the server answered with no real message (a rolling deploy /
 *    blank gateway). A 5xx carrying a real message (e.g. the work-item commit
 *    route's 502 with the Azure DevOps failure) is a REAL backend error → its
 *    message must be shown, not replaced with the canned copy.
 *  - a 4xx ApiError (400/403/412/422 …) is a REAL client error → never this.
 *  - a fetch network-failure TypeError ("Failed to fetch") → true; any other
 *    TypeError is a real client bug → not this.
 *  - an aborted request, or the browser knowing it's offline.
 */
export function isServerUnreachable(e: unknown): boolean {
  if (e instanceof ApiError) {
    if (e.statusCode === 0) return true;
    if (e.statusCode < 500) return false;
    return isBlankGatewayError(e);
  }
  if (e instanceof TypeError) return NETWORK_FAILURE_RE.test(e.message);
  if (e instanceof DOMException && e.name === 'AbortError') return true;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return false;
}

/** Calm copy shown when a write fails because the server is briefly absent —
 *  the form stays open with the text intact, so the user just retries when it's
 *  back (we never auto-retry: these POSTs aren't idempotent). */
export const SERVER_UNREACHABLE_MESSAGE =
  "Couldn't reach the server — your text is safe, try again in a moment.";

/**
 * Pick the toast message for a FAILED submit — and record the failure to
 * diagnostics. Call this only from an error handler (it always treats `e` as a
 * fault).
 *
 * Message: the calm "server unreachable" copy for a transient outage, else the
 * error's own (real) message.
 *
 * Side effect: a genuine, user-visible failure is reported to the unified
 * app_errors store via reportClientError. Handled errors like this become a
 * toast and are caught locally, so they NEVER reach window.onerror /
 * unhandledrejection — without this they're invisible to Diagnostics + the MCP
 * tools (which is exactly why a failed "create work item from topic" recorded
 * nothing client-side). We skip transient outages (isServerUnreachable) and the
 * expected "connect your account" 412 flow so the store stays high-signal.
 * reportClientError is fire-and-forget, dedups per `${kind}:${message}`, and
 * never throws, so this is safe to call from every submit error handler.
 */
export function submitErrorMessage(e: unknown, fallback = 'Action failed'): string {
  if (isServerUnreachable(e)) return SERVER_UNREACHABLE_MESSAGE;
  // Record the failure — but not the EXPECTED, user-actionable rejections, which
  // share one shared skip-list with the server tee (USER_ACTIONABLE_ERROR_CODES):
  // 412 connection_required (the inline "connect your account" flow), 403
  // permission_denied (gated UI is already hidden), and 403 ado_permission_denied
  // (Azure DevOps refused the user's OWN account — they're told to ask IT support,
  // b-01779). Everything else — real 4xx/5xx app faults and unexpected client bugs
  // — is worth a row.
  const expected = e instanceof ApiError && isUserActionableErrorCode(e.code);
  if (!expected) reportClientError(e, { kind: 'handled.submit' });
  return e instanceof Error ? e.message : fallback;
}
